import { PERMISSIONS, type Permission } from '@optiflow/types';

/** A leaf entry inside a navigation group. */
export interface NavLeaf {
  key: string;
  labelKey: string;
  to: string;
  permission?: Permission;
  /** Route exists and is reachable, but renders a placeholder until its module ships. */
  comingSoon?: boolean;
}

/** A sidebar entry: either a direct link (`to`, no children) or an expandable group. */
export interface NavItem {
  key: string;
  labelKey: string;
  icon: string;
  to?: string;
  permission?: Permission;
  comingSoon?: boolean;
  children?: NavLeaf[];
}

export interface NavSection {
  key: string;
  labelKey?: string;
  /** Rendered only for SaaS-provider staff. */
  platformOnly?: boolean;
  items: NavItem[];
}

/**
 * The single source of truth for the sidebar *and* the router.
 *
 * Every entry declares the permission it needs as data. Nothing here decides who sees what — that
 * is `visibleNavigation`, a pure function the sidebar calls — so permission logic is never
 * embedded in a component and can be tested on its own.
 *
 * Permission strings come from the shared catalogue, so an entry pointing at a permission that no
 * longer exists is a compile error rather than a menu item nobody can reach.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    key: 'main',
    items: [{ key: 'dashboard', labelKey: 'nav.dashboard', icon: 'LayoutDashboard', to: '/' }],
  },
  {
    key: 'operations',
    labelKey: 'nav.sections.operations',
    items: [
      {
        key: 'sales',
        labelKey: 'nav.sales.group',
        icon: 'ShoppingCart',
        children: [
          {
            key: 'sales.new',
            labelKey: 'nav.sales.new',
            to: '/sales/counter',
            permission: PERMISSIONS.SALES_CREATE,
          },
          {
            key: 'sales.orders',
            labelKey: 'nav.sales.orders',
            to: '/sales/orders',
            permission: PERMISSIONS.SALES_VIEW,
          },
          {
            key: 'sales.optical',
            labelKey: 'nav.sales.optical',
            to: '/sales/optical-jobs',
            permission: PERMISSIONS.OPTICAL_VIEW,
          },
          {
            key: 'sales.receivables',
            labelKey: 'nav.sales.receivables',
            to: '/sales/receivables',
            permission: PERMISSIONS.SALES_VIEW,
          },
          {
            key: 'sales.returns',
            labelKey: 'nav.sales.returns',
            to: '/sales/returns',
            permission: PERMISSIONS.SALES_VIEW,
          },
          {
            key: 'sales.invoices',
            labelKey: 'nav.sales.invoices',
            to: '/sales/invoices',
            permission: PERMISSIONS.SALES_VIEW,
          },
        ],
      },
      {
        key: 'customers',
        labelKey: 'nav.customers.group',
        icon: 'Users',
        children: [
          // Implemented in Phase 1A.
          {
            key: 'customers.list',
            labelKey: 'nav.customers.list',
            to: '/customers',
            permission: PERMISSIONS.CUSTOMERS_VIEW,
          },
          // Eye examinations live on each customer's profile, so this entry opens the customer list to choose one.
          {
            key: 'customers.examinations',
            labelKey: 'nav.customers.examinations',
            to: '/customers/examinations',
            permission: PERMISSIONS.MEDICAL_VIEW,
          },
          {
            key: 'customers.measurements',
            labelKey: 'nav.customers.measurements',
            to: '/customers/measurements',
            permission: PERMISSIONS.MEASUREMENTS_VIEW,
            comingSoon: true,
          },
        ],
      },
      {
        key: 'inventory',
        labelKey: 'nav.inventory.group',
        icon: 'Package',
        children: [
          // Implemented in Phase 1B.
          {
            key: 'inventory.products',
            labelKey: 'nav.inventory.products',
            to: '/inventory/products',
            permission: PERMISSIONS.CATALOG_VIEW,
          },
          {
            key: 'inventory.categories',
            labelKey: 'nav.inventory.categories',
            to: '/inventory/categories',
            permission: PERMISSIONS.CATALOG_VIEW,
          },
          {
            key: 'inventory.stock',
            labelKey: 'nav.inventory.stock',
            to: '/inventory/stock',
            permission: PERMISSIONS.INVENTORY_VIEW,
          },
          {
            key: 'inventory.transfers',
            labelKey: 'nav.inventory.transfers',
            to: '/inventory/transfers',
            permission: PERMISSIONS.INVENTORY_TRANSFER,
          },
          {
            key: 'inventory.stocktake',
            labelKey: 'nav.inventory.stocktake',
            to: '/inventory/stocktake',
            permission: PERMISSIONS.INVENTORY_VIEW,
          },
          {
            key: 'inventory.alerts',
            labelKey: 'nav.inventory.alerts',
            to: '/inventory/alerts',
            permission: PERMISSIONS.INVENTORY_VIEW,
          },
        ],
      },
      {
        key: 'purchasing',
        labelKey: 'nav.purchasing.group',
        icon: 'Truck',
        children: [
          {
            key: 'purchasing.orders',
            labelKey: 'nav.purchasing.orders',
            to: '/purchasing/orders',
            permission: PERMISSIONS.PURCHASING_VIEW,
          },
          {
            key: 'purchasing.receipts',
            labelKey: 'nav.purchasing.receipts',
            to: '/purchasing/receipts',
            permission: PERMISSIONS.PURCHASING_VIEW,
          },
          {
            key: 'purchasing.invoices',
            labelKey: 'nav.purchasing.invoices',
            to: '/purchasing/invoices',
            permission: PERMISSIONS.PURCHASING_VIEW,
          },
          {
            key: 'purchasing.suppliers',
            labelKey: 'nav.purchasing.suppliers',
            to: '/purchasing/suppliers',
            permission: PERMISSIONS.SUPPLIERS_VIEW,
          },
        ],
      },
      {
        key: 'wholesale',
        labelKey: 'nav.wholesale',
        icon: 'Warehouse',
        to: '/wholesale',
        permission: PERMISSIONS.WHOLESALE_VIEW,
        comingSoon: true,
      },
    ],
  },
  {
    key: 'finance',
    labelKey: 'nav.sections.finance',
    items: [
      {
        key: 'finance',
        labelKey: 'nav.finance.group',
        icon: 'Wallet',
        children: [
          {
            key: 'finance.payments',
            labelKey: 'nav.finance.payments',
            to: '/finance/payments',
            permission: PERMISSIONS.PAYMENTS_VIEW,
            comingSoon: true,
          },
          {
            // `expenses.view` and never the deprecated `expenses.create` or `expenses.approve`:
            // those predate Phase 4.4 and authorise nothing.
            key: 'finance.expenses',
            labelKey: 'nav.finance.expenses',
            to: '/finance/expenses',
            permission: PERMISSIONS.EXPENSES_VIEW,
          },
          {
            // Its own capability, never purchasing's: an accountant settles invoices without
            // gaining the power to place or cancel orders, and a buyer does not gain the power to
            // move money by being able to raise one.
            key: 'finance.payables',
            labelKey: 'nav.finance.payables',
            to: '/finance/payables',
            permission: PERMISSIONS.PAYABLES_VIEW,
          },
        ],
      },
      {
        key: 'reports',
        labelKey: 'nav.reports',
        icon: 'BarChart3',
        to: '/reports',
        permission: PERMISSIONS.REPORTING_VIEW,
      },
    ],
  },
  {
    key: 'people',
    labelKey: 'nav.sections.people',
    items: [
      {
        key: 'employees',
        labelKey: 'nav.employees.group',
        icon: 'UserCog',
        children: [
          {
            key: 'employees.list',
            labelKey: 'nav.employees.list',
            to: '/employees',
            permission: PERMISSIONS.USERS_VIEW,
            comingSoon: true,
          },
          {
            key: 'employees.attendance',
            labelKey: 'nav.employees.attendance',
            to: '/employees/attendance',
            permission: PERMISSIONS.ATTENDANCE_VIEW,
            comingSoon: true,
          },
        ],
      },
      {
        key: 'crm',
        labelKey: 'nav.crm.group',
        icon: 'MessageSquare',
        children: [
          {
            key: 'crm.notifications',
            labelKey: 'nav.crm.notifications',
            to: '/crm/notifications',
            permission: PERMISSIONS.NOTIFICATIONS_VIEW,
          },
          {
            key: 'crm.automations',
            labelKey: 'nav.crm.automations',
            to: '/crm/automations',
            permission: PERMISSIONS.AUTOMATIONS_VIEW,
          },
          {
            key: 'crm.templates',
            labelKey: 'nav.crm.templates',
            to: '/crm/templates',
            permission: PERMISSIONS.NOTIFICATIONS_VIEW,
          },
        ],
      },
    ],
  },
  {
    key: 'settings',
    labelKey: 'nav.sections.settings',
    items: [
      {
        key: 'settings',
        labelKey: 'nav.settings.group',
        icon: 'Settings',
        children: [
          {
            key: 'settings.company',
            labelKey: 'nav.settings.company',
            to: '/settings/company',
            // Changing the business profile is tenant settings; the page reads and writes only with tenant.update.
            permission: PERMISSIONS.TENANT_UPDATE,
          },
          // Implemented against the real Branches API.
          {
            key: 'settings.branches',
            labelKey: 'nav.settings.branches',
            to: '/settings/branches',
            permission: PERMISSIONS.BRANCHES_VIEW,
          },
          {
            key: 'settings.opticalLabs',
            labelKey: 'nav.settings.opticalLabs',
            to: '/settings/optical-labs',
            permission: PERMISSIONS.OPTICAL_MANAGE_LABS,
          },
          {
            key: 'settings.users',
            labelKey: 'nav.settings.users',
            to: '/settings/users',
            permission: PERMISSIONS.USERS_VIEW,
            comingSoon: true,
          },
          {
            key: 'settings.roles',
            labelKey: 'nav.settings.roles',
            to: '/settings/roles',
            permission: PERMISSIONS.ROLES_VIEW,
            comingSoon: true,
          },
          {
            key: 'settings.paymentMethods',
            labelKey: 'nav.settings.paymentMethods',
            to: '/settings/payment-methods',
            permission: PERMISSIONS.PAYMENTS_VIEW,
            comingSoon: true,
          },
          {
            key: 'settings.whatsapp',
            labelKey: 'nav.settings.whatsapp',
            to: '/settings/whatsapp',
            // Reading which number a branch messages from is operational, not administrative; the
            // page hides every control from whoever cannot use it.
            permission: PERMISSIONS.NOTIFICATIONS_VIEW,
          },
          {
            // Tenant policy: the week start and the optical attention thresholds reports use.
            key: 'settings.reporting',
            labelKey: 'nav.settings.reporting',
            to: '/settings/reporting',
            permission: PERMISSIONS.TENANT_UPDATE,
          },
          {
            key: 'settings.printing',
            labelKey: 'nav.settings.printing',
            to: '/settings/printing',
            permission: PERMISSIONS.TENANT_UPDATE,
          },
          {
            key: 'settings.integrations',
            labelKey: 'nav.settings.integrations',
            to: '/settings/integrations',
            permission: PERMISSIONS.TENANT_UPDATE,
            comingSoon: true,
          },
        ],
      },
    ],
  },
  {
    key: 'platform',
    labelKey: 'nav.sections.platform',
    platformOnly: true,
    items: [
      {
        key: 'platform.tenants',
        labelKey: 'nav.platform.tenants',
        icon: 'Building2',
        to: '/platform/tenants',
        permission: PERMISSIONS.PLATFORM_TENANTS_VIEW,
        comingSoon: true,
      },
    ],
  },
];

/**
 * Filters the tree down to what a principal may see.
 *
 * A group survives only if at least one of its children survives, so a manager without any
 * finance permission never sees an empty "Finance" heading. Pure and dependency-free, which is
 * what lets `navigation.test.ts` cover it without rendering anything.
 */
export function visibleNavigation(
  can: (permission: string) => boolean,
  isPlatformUser: boolean,
): NavSection[] {
  return NAV_SECTIONS.filter((section) => !section.platformOnly || isPlatformUser)
    .map((section) => ({
      ...section,
      items: section.items
        .map((item) => {
          if (!item.children) return item;
          const children = item.children.filter(
            (child) => !child.permission || can(child.permission),
          );
          return { ...item, children };
        })
        .filter((item) =>
          item.children ? item.children.length > 0 : !item.permission || can(item.permission),
        ),
    }))
    .filter((section) => section.items.length > 0);
}

/** Every routable path in the tree, used to build the router from the same source. */
export function navigationRoutes(): NavLeaf[] {
  return NAV_SECTIONS.flatMap((section) =>
    section.items.flatMap(
      (item) => item.children ?? (item.to ? [{ ...item, to: item.to } as NavLeaf] : []),
    ),
  );
}
